import Icon from '@/components/ui/AppIcon';

interface BusinessHour {
  day: string;
  hours: string;
  isToday: boolean;
}

interface BusinessHoursProps {
  currentLanguage: 'fr' | 'de';
}

const BusinessHours = ({ currentLanguage }: BusinessHoursProps) => {
  const content = {
    fr: {
      title: "Heures d\'ouverture",
      subtitle: "Nous sommes disponibles pour vous servir",
      emergency: "Service d\'urgence 24/7",
      emergencyDesc: "Pour les urgences de nettoyage, appelez-nous à tout moment",
      emergencyPhone: "+41 76 212 65 06",
      schedule: [
        { day: "Lundi", hours: "7h00 - 20h00", isToday: false },
        { day: "Mardi", hours: "7h00 - 20h00", isToday: false },
        { day: "Mercredi", hours: "7h00 - 20h00", isToday: true },
        { day: "Jeudi", hours: "7h00 - 20h00", isToday: false },
        { day: "Vendredi", hours: "7h00 - 20h00", isToday: false },
        { day: "Samedi", hours: "8h00 - 18h00", isToday: false },
        { day: "Dimanche", hours: "9h00 - 17h00", isToday: false }
      ] as BusinessHour[],
      note: "Les heures peuvent varier pendant les jours fériés"
    },
    de: {
      title: "Öffnungszeiten",
      subtitle: "Wir sind verfügbar, um Ihnen zu dienen",
      emergency: "24/7 Notdienst",
      emergencyDesc: "Für Reinigungsnotfälle rufen Sie uns jederzeit an",
      emergencyPhone: "+41 76 212 65 06",
      schedule: [
        { day: "Montag", hours: "7:00 - 20:00", isToday: false },
        { day: "Dienstag", hours: "7:00 - 20:00", isToday: false },
        { day: "Mittwoch", hours: "7:00 - 20:00", isToday: true },
        { day: "Donnerstag", hours: "7:00 - 20:00", isToday: false },
        { day: "Freitag", hours: "7:00 - 20:00", isToday: false },
        { day: "Samstag", hours: "8:00 - 18:00", isToday: false },
        { day: "Sonntag", hours: "9:00 - 17:00", isToday: false }
      ] as BusinessHour[],
      note: "Die Öffnungszeiten können an Feiertagen variieren"
    }
  };

  const text = content[currentLanguage];

  return (
    <section className="py-16 lg:py-24 bg-surface">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-heading font-bold text-foreground mb-4">
              {text.title}
            </h2>
            <p className="text-lg text-muted-foreground">
              {text.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Schedule */}
            <div className="lg:col-span-2">
              <div className="bg-card rounded-2xl shadow-lg p-8">
                <div className="space-y-4">
                  {text.schedule.map((item, index) => (
                    <div
                      key={index}
                      className={`flex items-center justify-between p-4 rounded-lg transition-all duration-300 ${
                        item.isToday
                          ? 'bg-primary/10 border-2 border-primary' :'bg-surface hover:bg-muted'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {item.isToday && (
                          <Icon name="ClockIcon" size={20} variant="solid" className="text-primary" />
                        )}
                        <span className={`font-medium ${item.isToday ? 'text-primary' : 'text-foreground'}`}>
                          {item.day}
                        </span>
                      </div>
                      <span className={`font-mono ${item.isToday ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>
                        {item.hours}
                      </span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-border">
                  <div className="flex items-start gap-3 text-sm text-muted-foreground">
                    <Icon name="InformationCircleIcon" size={20} variant="outline" className="flex-shrink-0 mt-0.5" />
                    <p>{text.note}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Emergency Contact */}
            <div>
              <div className="bg-gradient-to-br from-destructive to-warning rounded-2xl shadow-lg p-8 text-white">
                <div className="flex items-center justify-center w-16 h-16 bg-white/20 backdrop-blur-sm rounded-xl mb-6 mx-auto">
                  <Icon name="ExclamationTriangleIcon" size={32} variant="solid" className="text-white" />
                </div>

                <h3 className="text-xl font-heading font-bold text-center mb-3">
                  {text.emergency}
                </h3>

                <p className="text-sm text-white/90 text-center mb-6">
                  {text.emergencyDesc}
                </p>

                <a
                  href={`tel:${text.emergencyPhone.replace(/\s/g, '')}`}
                  className="flex items-center justify-center gap-3 bg-white text-destructive font-heading font-semibold py-3 px-6 rounded-lg hover:bg-white/90 transition-all duration-300 shadow-md"
                >
                  <Icon name="PhoneIcon" size={20} variant="solid" />
                  <span>{text.emergencyPhone}</span>
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default BusinessHours;